import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPost, posts } from '../blog';
import { PostArticle } from '../blog/PostArticle';
import type { LinkComponent } from '../blog/LinkComponent';
import { SITE_URL, postJsonLd, postUrl } from '../blog/seo';
import { useHead } from '../lib/useHead';

const RouterLink: LinkComponent = ({ to, className, children }) => (
  <Link to={to} className={className}>
    {children}
  </Link>
);

const NotFound: React.FC = () => (
  <div className="max-w-3xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-24 text-center">
    <h1 className="font-display font-bold text-[32px] md:text-[44px] tracking-[-0.03em] text-ink m-0">That post has moved</h1>
    <p className="mt-4 text-[17px] text-ink-muted">It might have a new address. Everything we have published is on the blog.</p>
    <Link to="/blog" className="inline-flex items-center h-12 px-6 mt-8 rounded-pill bg-ink text-white font-semibold no-underline">
      Go to the blog
    </Link>
  </div>
);

const BlogPost: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const post = getPost(slug);

  useHead(
    post
      ? {
          title: `${post.title} | EasyToCook`,
          description: post.description,
          canonical: postUrl(post),
          ogType: 'article',
          ogImage: `${SITE_URL}${post.ogImage}`,
          jsonLd: postJsonLd(post),
          extra: [
            { property: 'article:published_time', content: post.publishedAt },
            { property: 'article:modified_time', content: post.updatedAt },
            ...post.tags.map((t) => ({ property: 'article:tag', content: t })),
          ],
        }
      : { title: 'Post not found | EasyToCook', description: 'That post is not here any more.' },
  );

  if (!post) return <NotFound />;

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 3);
  return <PostArticle post={post} Link={RouterLink} related={related} />;
};

export default BlogPost;
